/**
 * Camera Service - Frontend API calls for camera management
 */

const API_BASE_URL = '/api/v1';

export interface IPCamera {
  id: number;
  name: string;
  description: string;
  camera_type: string;
  camera_type_display: string;
  ip_address: string;
  rtsp_url: string;
  username: string;
  rtsp_port: number;
  site: number;
  site_name: string;
  location: number | null;
  location_name: string | null;
  plant_area: number | null;
  plant_area_name: string | null;
  overview_position_x: number | null;
  overview_position_y: number | null;
  default_quality: string;
  default_quality_display: string;
  update_interval: number;
  enable_motion_detection: boolean;
  is_public: boolean;
  is_active: boolean;
  connection_status: string;
  connection_status_display: string;
  last_seen: string | null;
  stream_url: string;
  thumbnail_url: string;
  equipment_names: string[];
  equipment_count: number;
  created_at: string;
  updated_at: string;
}

export interface CameraRecording {
  id: number;
  camera: number;
  camera_name: string;
  recording_type: string;
  recording_type_display: string;
  start_time: string;
  end_time: string | null;
  duration_seconds: number | null;
  duration_formatted: string | null;
  file_path: string;
  file_size: number | null;
  file_size_mb: number | null;
  quality: string;
  quality_display: string;
  permit: number | null;
  permit_number: string | null;
  maintenance_task: number | null;
  maintenance_task_name: string | null;
  notes: string;
  recorded_by: number | null;
  recorded_by_name: string | null;
  created_at: string;
}

export interface CameraAlert {
  id: number;
  camera: number;
  camera_name: string;
  alert_type: string;
  alert_type_display: string;
  severity: string;
  severity_display: string;
  message: string;
  detected_at: string;
  acknowledged_at: string | null;
  acknowledged_by: number | null;
  acknowledged_by_name: string | null;
  thumbnail_path: string;
  video_clip_path: string;
  is_active: boolean;
  resolved_at: string | null;
  resolution_notes: string;
  time_since_detection: string;
}

export interface CameraSummary {
  total_cameras: number;
  online_cameras: number;
  offline_cameras: number;
  error_cameras: number;
  active_alerts: number;
  recent_recordings: number;
}

export interface CameraTestResult {
  success: boolean;
  message: string;
  connection_status?: string;
  resolution?: string;
}

export interface RecordingStartResult {
  success: boolean;
  recording_id?: number;
  message: string;
}

class CameraService {
  
  // Camera CRUD operations
  async getCameras(siteId?: number): Promise<{ results: IPCamera[], count: number }> {
    const params = new URLSearchParams();
    if (siteId && siteId !== null) params.append('site_id', siteId.toString());
    
    const url = `${API_BASE_URL}/sites/cameras/?${params}`;
    console.log('🔍 CameraService: Fetching cameras from:', url);
    
    try {
      const response = await fetch(url, {
        credentials: 'include'
      });
      
      console.log('🔍 CameraService: Response status:', response.status);
      console.log('🔍 CameraService: Response headers:', Array.from(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ CameraService: Failed to fetch cameras:', response.status, errorText);
        throw new Error(`Failed to fetch cameras: ${response.status} ${errorText}`);
      }
      
      const data = await response.json();
      console.log('✅ CameraService: Successfully fetched cameras:', data);
      return data;
    } catch (error) {
      console.error('❌ CameraService: Error fetching cameras:', error);
      throw error;
    }
  }
  
  async getCamera(id: number): Promise<IPCamera> {
    const response = await fetch(`${API_BASE_URL}/sites/cameras/${id}/`, {
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch camera');
    }
    
    return response.json();
  }
  
  async createCamera(camera: Partial<IPCamera>): Promise<IPCamera> {
    const response = await fetch(`${API_BASE_URL}/sites/cameras/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(camera)
    });
    
    if (!response.ok) {
      throw new Error('Failed to create camera');
    }
    
    return response.json();
  }
  
  async updateCamera(id: number, camera: Partial<IPCamera>): Promise<IPCamera> {
    const response = await fetch(`${API_BASE_URL}/sites/cameras/${id}/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(camera)
    });
    
    if (!response.ok) {
      throw new Error('Failed to update camera');
    }
    
    return response.json();
  }
  
  async deleteCamera(id: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/sites/cameras/${id}/`, {
      method: 'DELETE',
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error('Failed to delete camera');
    }
  }
  
  // Camera streaming and control
  getCameraStreamUrl(id: number, quality: string = 'medium'): string {
    return `${API_BASE_URL}/sites/cameras/${id}/stream/?quality=${quality}`;
  }
  
  getCameraThumbnailUrl(id: number): string {
    return `${API_BASE_URL}/sites/cameras/${id}/thumbnail/`;
  }

  // Use Django-served cached full image endpoint to match current deployment
  getCameraFullImageUrl(id: number): string {
    return `${API_BASE_URL}/sites/api/cameras/${id}/image/full/`;
  }

  getCameraLiveStreamUrl(id: number, quality: string = 'medium'): string {
    // Prefer H.264 passthrough which our backend proxies to remote in copy mode
    return `${API_BASE_URL}/sites/cameras/${id}/stream/h264/?quality=${quality}`;
  }

  getCameraDirectStreamUrl(id: number, quality: string = 'medium'): string {
    // Historically used to hit the passthrough/restreamer URL; keep quality only for legacy endpoints
    return `${API_BASE_URL}/sites/cameras/${id}/stream/`;
  }

  getCameraH264StreamUrl(id: number, quality: string = 'medium'): string {
    return `${API_BASE_URL}/sites/cameras/${id}/stream/h264/?quality=${quality}`;
  }

  getCameraFullSnapshotUrl(id: number): string {
    return `${API_BASE_URL}/sites/cameras/${id}/snapshot/full/`;
  }

  async captureFullSnapshot(id: number): Promise<Blob> {
    try {
      const response = await fetch(`${API_BASE_URL}/sites/cameras/${id}/snapshot/full/`, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      console.error('Failed to capture full snapshot:', error);
      throw error;
    }
  }

  async getThumbnailInfo(id: number): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/sites/cameras/${id}/thumbnail-info/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get thumbnail info:', error);
      throw error;
    }
  }

  async captureThumbnail(id: number): Promise<{ success: boolean; message: string; timestamp?: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/sites/cameras/${id}/thumbnail/capture/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to capture thumbnail:', error);
      throw error;
    }
  }
  
  async testCameraConnection(id: number): Promise<CameraTestResult> {
    const response = await fetch(`${API_BASE_URL}/sites/cameras/${id}/test_connection/`, {
      method: 'POST',
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error('Failed to test camera connection');
    }
    
    return response.json();
  }
  
  async startRecording(id: number, options: {
    recording_type?: string;
    quality?: string;
    notes?: string;
  }): Promise<RecordingStartResult> {
    const response = await fetch(`${API_BASE_URL}/sites/cameras/${id}/start_recording/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(options)
    });
    
    if (!response.ok) {
      throw new Error('Failed to start recording');
    }
    
    return response.json();
  }
  
  // Camera summary and dashboard
  async getCameraSummary(siteId?: number): Promise<CameraSummary> {
    const params = new URLSearchParams();
    if (siteId) params.append('site_id', siteId.toString());
    
    const url = `${API_BASE_URL}/sites/cameras/summary/?${params}`;
    console.log('🔍 CameraService: Fetching camera summary from:', url);
    
    try {
      const response = await fetch(url, {
        credentials: 'include'
      });
      
      console.log('🔍 CameraService: Summary response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ CameraService: Failed to fetch camera summary:', response.status, errorText);
        throw new Error(`Failed to fetch camera summary: ${response.status} ${errorText}`);
      }
      
      const data = await response.json();
      console.log('✅ CameraService: Successfully fetched camera summary:', data);
      return data;
    } catch (error) {
      console.error('❌ CameraService: Error fetching camera summary:', error);
      throw error;
    }
  }
  
  // Recording management
  async getRecordings(cameraId?: number): Promise<{ results: CameraRecording[], count: number }> {
    const params = new URLSearchParams();
    if (cameraId) params.append('camera', cameraId.toString());
    
    const response = await fetch(`${API_BASE_URL}/sites/camera-recordings/?${params}`, {
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch recordings');
    }
    
    return response.json();
  }
  
  async downloadRecording(id: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/sites/camera-recordings/${id}/download/`, {
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error('Failed to download recording');
    }
    
    // Create download link
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recording_${id}.mp4`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
  
  // Alert management
  async getAlerts(cameraId?: number): Promise<{ results: CameraAlert[], count: number }> {
    const params = new URLSearchParams();
    if (cameraId) params.append('camera', cameraId.toString());
    
    const response = await fetch(`${API_BASE_URL}/sites/camera-alerts/?${params}`, {
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch alerts');
    }
    
    return response.json();
  }
  
  async acknowledgeAlert(id: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/sites/camera-alerts/${id}/acknowledge/`, {
      method: 'POST',
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error('Failed to acknowledge alert');
    }
  }
  
  async resolveAlert(id: number, resolutionNotes: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/sites/camera-alerts/${id}/resolve/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ resolution_notes: resolutionNotes })
    });
    
    if (!response.ok) {
      throw new Error('Failed to resolve alert');
    }
  }
}

export const cameraService = new CameraService();
export default cameraService; 